import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import type { VoidCopyOptions } from './voidTypes';

// Re-exported for the tabs and the rejected action that import it from here.
export type { VoidCopyOptions } from './voidTypes';

export interface VoidClaimsConfirmDialogProps {
  open: boolean;
  selectedCount: number;
  /** True on tabs at/past Pending Payment, where the claim may already be with the payer. */
  postSend: boolean;
  /** Hide when this path rebuilds worksheet values instead of copying them. */
  showCopyWorksheetOption?: boolean;
  /** Draft cycles carry nothing to copy forward, so no copy options are offered. */
  draft?: boolean;
  /** Extra sentence(s) shown as a second paragraph under the main message. */
  appendedMessage?: string;
  onConfirm: (options: VoidCopyOptions) => void;
  onCancel: () => void;
}

const baseMessage = (count: number) =>
  count === 1
    ? 'This voids 1 billing cycle in Roger and releases its visits for rebilling. The period is rebuilt once due. Notes are copied only to a replacement created now.'
    : `This voids ${count} billing cycles in Roger and releases their visits for rebilling. The periods are rebuilt once due. Notes are copied only to a replacement created now.`;

const draftMessage = (count: number) =>
  count === 1
    ? 'This voids 1 draft billing cycle in Roger and releases its visits. The voided period is rebuilt on the next billing cycle refresh if nothing else covers it.'
    : `This voids ${count} draft billing cycles in Roger and releases their visits. The voided periods are rebuilt on the next billing cycle refresh if nothing else covers them.`;

/**
 * Shown under the draft void message on the Ready and Not Ready tabs. A draft
 * can carry biller notes, and a draft void creates no replacement in the same
 * call, so nothing copies them to the period the nightly pass rebuilds.
 */
export const DRAFT_VOID_NOTES_WARNING =
  'Notes are not carried over to the rebuilt period.';

const PAYER_WARNING =
  'The payer is not notified — if a claim was already submitted or paid, ' +
  'you must cancel it with the payer separately.';

export const VoidClaimsConfirmDialog: React.FC<
  VoidClaimsConfirmDialogProps
> = ({
  open,
  selectedCount,
  postSend,
  showCopyWorksheetOption = true,
  draft = false,
  appendedMessage,
  onConfirm,
  onCancel,
}) => {
  const [copyWorksheetForward, setCopyWorksheetForward] = useState(true);
  const [copyNotesForward, setCopyNotesForward] = useState(true);

  useEffect(() => {
    if (open) {
      setCopyWorksheetForward(true);
      setCopyNotesForward(true);
    }
  }, [open]);

  let message = postSend
    ? `${baseMessage(selectedCount)} ${PAYER_WARNING}`
    : baseMessage(selectedCount);
  if (draft) {
    message = draftMessage(selectedCount);
  } else if (!showCopyWorksheetOption) {
    message +=
      ' Use Regenerate claims on the Not Ready or Ready tab to rebuild worksheet values.';
  }
  const showWorksheetOption = showCopyWorksheetOption && !draft;

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) {
          onCancel();
        }
      }}
    >
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>Void claims?</DialogTitle>
          <DialogDescription>{message}</DialogDescription>
          {appendedMessage ? (
            // A sibling of DialogDescription (which is itself a <p>) so the
            // extra paragraph never nests a <p> inside a <p>.
            <p className='text-sm text-muted-foreground'>{appendedMessage}</p>
          ) : null}
        </DialogHeader>
        {draft ? null : (
          <div className='flex flex-col gap-3 py-2'>
            {showWorksheetOption ? (
              <label className='flex items-center gap-2 text-sm'>
                <Checkbox
                  checked={copyWorksheetForward}
                  onCheckedChange={checked =>
                    setCopyWorksheetForward(checked === true)
                  }
                  checkboxSize='sm'
                />
                Copy claim worksheet
              </label>
            ) : null}
            <label className='flex items-center gap-2 text-sm'>
              <Checkbox
                checked={copyNotesForward}
                onCheckedChange={checked =>
                  setCopyNotesForward(checked === true)
                }
                checkboxSize='sm'
              />
              Copy notes
            </label>
          </div>
        )}
        <DialogFooter>
          <Button variant='outline' onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              onConfirm({
                copyWorksheetForward:
                  showWorksheetOption && copyWorksheetForward,
                copyNotesForward: !draft && copyNotesForward,
              })
            }
          >
            Void
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export interface VoidClaimsButtonProps {
  selectedCount: number;
  postSend: boolean;
  disabled: boolean;
  isSubmitting: boolean;
  onConfirm: (options: VoidCopyOptions) => void;
}

export const VoidClaimsButton: React.FC<VoidClaimsButtonProps> = ({
  selectedCount,
  postSend,
  disabled,
  isSubmitting,
  onConfirm,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <>
      <VoidClaimsConfirmDialog
        open={confirmOpen}
        selectedCount={selectedCount}
        postSend={postSend}
        onConfirm={options => {
          setConfirmOpen(false);
          onConfirm(options);
        }}
        onCancel={() => setConfirmOpen(false)}
      />
      <Button
        variant='destructive'
        disabled={disabled}
        onClick={() => setConfirmOpen(true)}
      >
        {isSubmitting && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
        Void
      </Button>
    </>
  );
};
